import { useState } from "react";
import { Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { trackEvent } from "@/lib/ga4";

/**
 * Pre-signup email capture for the Discover flow (HG-0790).
 *
 * A single email field shown at peak intent (clone overlay / end of demo). On
 * submit it POSTs to the Operations `discover-leads` edge function, which inserts
 * one row into discover_leads. HG-CC's send-discover-followup.mjs later sends one
 * gated follow-up per lead. This is additive — it never gates the signup path.
 *
 * Spec: HG-CC helpgenie/specs/discover-voice-instrumentation-spec.md (Section 2).
 */

const DISCOVER_LEADS_ENDPOINT =
  import.meta.env.VITE_DISCOVER_LEADS_ENDPOINT ??
  "https://nchqddbejgojpskedrha.supabase.co/functions/v1/discover-leads";

const SUBMITTED_KEY = "hg_discover_lead_submitted";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface DiscoverLeadCaptureProps {
  personaId: string;
  industry?: string;
  subcategory?: string;
  demoUrl?: string;
  captureLocation: "clone_overlay" | "end_of_demo" | "exit_intent";
  onSuccess?: () => void;
  primaryColor?: string;
  secondaryColor?: string;
}

const alreadySubmitted = () => {
  try {
    return !!window.localStorage.getItem(SUBMITTED_KEY);
  } catch {
    return false;
  }
};

export const DiscoverLeadCapture = ({
  personaId,
  industry,
  subcategory,
  demoUrl,
  captureLocation,
  onSuccess,
  primaryColor = "#4E9CFF",
  secondaryColor = "#FF4DB8",
}: DiscoverLeadCaptureProps) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "done" | "error">(
    alreadySubmitted() ? "done" : "idle",
  );
  const [touched, setTouched] = useState(false);

  // Already captured this browser session — don't show the form again.
  if (status === "done" && alreadySubmitted()) return null;

  const isValid = EMAIL_RE.test(email.trim());

  const handleSubmit = async () => {
    if (!isValid || status === "submitting") {
      setTouched(true);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(DISCOVER_LEADS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          industry,
          subcategory,
          persona_id: personaId,
          demo_url: demoUrl,
          capture_location: captureLocation,
        }),
      });
      // 409 (duplicate) is treated as success by the endpoint; any 2xx is a win.
      if (!res.ok && res.status !== 409) throw new Error(`HTTP ${res.status}`);

      try {
        window.localStorage.setItem(SUBMITTED_KEY, email.trim());
      } catch {
        /* localStorage may be blocked; the capture still succeeded */
      }
      trackEvent("discover_lead_captured", {
        industry,
        subcategory,
        persona_id: personaId,
        capture_location: captureLocation,
      });
      setStatus("done");
      onSuccess?.();
    } catch {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "12px 14px",
          borderRadius: 12,
          background: `${primaryColor}0F`,
          border: `1px solid ${primaryColor}25`,
          marginBottom: 14,
        }}
      >
        <Check style={{ width: 16, height: 16, color: primaryColor }} strokeWidth={3} />
        <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>
          Got it. Check your inbox in a few minutes.
        </span>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <p style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151", margin: "0 0 3px" }}>
        Want us to hold this genie for you?
      </p>
      <p style={{ fontSize: "0.72rem", color: "#9CA3AF", margin: "0 0 10px", lineHeight: 1.4 }}>
        Drop your email and we will send it back with a ready-to-claim link.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <Input
          type="email"
          inputMode="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          disabled={status === "submitting"}
          aria-label="Email address"
          style={{ borderRadius: 10, flex: 1 }}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={status === "submitting"}
          style={{
            flexShrink: 0,
            padding: "0 16px",
            borderRadius: 10,
            border: "none",
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            color: "white",
            fontWeight: 600,
            fontSize: "0.82rem",
            cursor: status === "submitting" ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          {status === "submitting" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Send my genie"
          )}
        </button>
      </div>
      {touched && !isValid && email.length > 0 && (
        <p style={{ fontSize: "0.7rem", color: "#EF4444", margin: "6px 0 0" }}>
          Please enter a valid email.
        </p>
      )}
      {status === "error" && (
        <p style={{ fontSize: "0.7rem", color: "#EF4444", margin: "6px 0 0" }}>
          Something went wrong. You can still create your genie below.
        </p>
      )}
    </div>
  );
};
